/**
 * SSE + pipeline + finalize→summary hand-off for the meeting-detail route.
 *
 * Owns `useMeetingEvents` callbacks, processing-stage / pipeline weight state,
 * and the `justFinalized` / `showSummary` reveal effect. `isLive` stays in the
 * route so the meeting query's `select` can flip it before this hook runs.
 */
import { LIVE_MEETING_STATUSES, SSE_EVENTS } from "@lexicanon/shared"
import { useQueryClient } from "@tanstack/react-query"
import { useCallback, useEffect, useRef, useState } from "react"
import { useMeetingEvents } from "../../../../hooks/useSSE"
import { withViewTransition } from "../../../../hooks/useViewTransition"
import {
  applyPipelineStep,
  type IPipelineWeights,
  isProcessingStage,
  pipelineStarted,
  type TProcessingStage,
} from "../../../../lib/pipeline-progress"
import { queryKeys } from "../../../../lib/query-keys"
import type { ITrackedMeeting } from "../../../../lib/types/streaming"

const LIVE_STATUSES = LIVE_MEETING_STATUSES

export interface IMeetingDetailEventsMeeting {
  status: string
  recording_url?: string | null
  analysis?: unknown
}

export interface IUseMeetingDetailEvents {
  processingStage: TProcessingStage | null
  pipeline: IPipelineWeights | null
  justFinalizedRef: React.MutableRefObject<boolean>
  showSummary: boolean
}

export function useMeetingDetailEvents(
  meetingId: string,
  orgId: string | null,
  product: string,
  meeting: IMeetingDetailEventsMeeting | undefined,
  setIsLive: (live: boolean) => void,
): IUseMeetingDetailEvents {
  const queryClient = useQueryClient()
  const [processingStage, setProcessingStage] = useState<TProcessingStage | null>(null)
  // Weighted, skip-excluded finalize progress (see `lib/pipeline-progress`). We
  // track weight (≈ wall-clock cost) rather than step count so the long
  // `final-analysis` summary dominates the bar.
  const [pipeline, setPipeline] = useState<IPipelineWeights | null>(null)
  // Finalize→summary reveal: `justFinalizedRef` records that this mount passed
  // through the finalizing screen, so the summary cascades in (and crossfades
  // from the dialog) — but a meeting opened already-summarised renders instantly.
  const justFinalizedRef = useRef(false)
  const [showSummary, setShowSummary] = useState(false)

  useMeetingEvents(
    useCallback(
      (tracked: ITrackedMeeting) => {
        const safeId = tracked.meetingId.replace(/[^a-zA-Z0-9_-]/g, "_")
        if (safeId !== meetingId) return
        setIsLive(LIVE_STATUSES.has(tracked.phase))
        queryClient.invalidateQueries({ queryKey: queryKeys.meeting(orgId, meetingId) })
      },
      [meetingId, orgId, queryClient, setIsLive],
    ),
    useCallback(
      (id: string, stage: string) => {
        const safeId = id.replace(/[^a-zA-Z0-9_-]/g, "_")
        if (safeId !== meetingId) return
        if (isProcessingStage(stage)) {
          setProcessingStage(stage)
        }
        if (stage === "complete") {
          // The analysis blob is persisted a beat AFTER the "complete" stage
          // fires, so pull the meeting now and once more shortly after — this is
          // what hands the finalize screen off to the summary instead of parking
          // it at 100%.
          queryClient.invalidateQueries({ queryKey: queryKeys.meeting(orgId, meetingId) })
          setTimeout(() => queryClient.invalidateQueries({ queryKey: queryKeys.meeting(orgId, meetingId) }), 1500)
          setTimeout(() => setProcessingStage(null), 2000)
        }
      },
      [meetingId, orgId, queryClient],
    ),
    useCallback(
      (mid: string, payload: Record<string, unknown>) => {
        const safeId = mid.replace(/[^a-zA-Z0-9_-]/g, "_")
        if (safeId !== meetingId) return
        if (payload.type === SSE_EVENTS.PIPELINE_STARTED) {
          setPipeline(pipelineStarted(payload))
          return
        }
        if (payload.type === SSE_EVENTS.PIPELINE_STEP) {
          setPipeline((prev) => applyPipelineStep(prev, payload))
        }
      },
      [meetingId],
    ),
    useCallback(
      (mid: string, _speakersVersion: number, _reason: string) => {
        const safeId = mid.replace(/[^a-zA-Z0-9_-]/g, "_")
        if (safeId !== meetingId) return
        // Any speaker mutation — this tab, another viewer, or the pipeline —
        // reconverges every surface without a reload: the detail page (this
        // meeting), the full transcript, and the list card that shows the roster.
        queryClient.invalidateQueries({ queryKey: queryKeys.meeting(orgId, meetingId) })
        queryClient.invalidateQueries({ queryKey: queryKeys.transcript(orgId, meetingId) })
        queryClient.invalidateQueries({ queryKey: queryKeys.meetings(orgId) })
      },
      [meetingId, orgId, queryClient],
    ),
  )

  // Drive the finalize→summary hand-off. Computed inline from the polled meeting
  // (kept before the early returns to satisfy the Rules of Hooks); the render
  // gates below use the precise `useLexicanonSummary` / `isFinalizingLex`.
  useEffect(() => {
    if (!meeting) return
    const s = meeting.status
    const finalizing = LIVE_STATUSES.has(s) || (!!meeting.recording_url && !meeting.analysis && s !== "error")
    const summaryReady = product === "lexicanon" && !!meeting.analysis && !LIVE_STATUSES.has(s) && s !== "error"
    if (finalizing) justFinalizedRef.current = true
    if (summaryReady && justFinalizedRef.current && !showSummary) {
      // Wrap the swap so the dialog crossfades into the summary (where supported).
      withViewTransition(() => setShowSummary(true))
    } else if (!summaryReady && showSummary) {
      setShowSummary(false)
    }
  }, [meeting, product, showSummary])

  return { processingStage, pipeline, justFinalizedRef, showSummary }
}
